"use client";

import { type FormEvent, useState, useEffect } from "react";
import { apiClient } from "@/lib/api/fetch-client";

import { updateProfile } from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { FormSanitizer } from "@/lib/utils/form-sanitizer";
import { API_CONFIG } from "@/lib/config/api";

interface EditProfileFormProps {
  userData: any;
  onSuccess: (updatedUser: any) => void;
  onCancel: () => void;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

const inputClass =
  "w-full rounded-brand border border-brand-border px-4 py-3 text-brand-fg transition-all focus:border-brand-primary focus:ring-2 focus:ring-brand-ring";

export function EditProfileForm({
  userData,
  onSuccess,
  onCancel,
}: EditProfileFormProps) {
  const { t } = usePageTranslation();
  const [formData, setFormData] = useState({
    name: userData.name || "",
    address: userData.address || "",
    division: userData.city || "", // Backend stores division in 'city' field
    city: "", // Actual city (not stored in backend yet, for future use)
    country: userData.country || "",
    postcode: userData.postcode || "",
    mobile: userData.mobile || "",
  });
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Location data
  const [countries, setCountries] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string | null>(null);
  const [selectedDivisionId, setSelectedDivisionId] = useState<string | null>(null);

  // Fetch countries on component mount and handle existing user data
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch divisions when country changes OR when countries are loaded with existing country data
  useEffect(() => {
    if (selectedCountryId) {
      fetchDivisions(selectedCountryId);
    } else {
      setDivisions([]);
      setCities([]);
    }
  }, [selectedCountryId]);

  // Fetch cities when division changes OR when divisions are loaded with existing division data
  useEffect(() => {
    if (selectedDivisionId) {
      fetchCities(selectedDivisionId);
    } else {
      setCities([]);
    }
  }, [selectedDivisionId]);

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiClient.get(`${API_CONFIG.BASE_URL}/api/public/countries`);

      console.log("📍 Countries response:", response.data);

      // API returns array directly, not wrapped in {status, data}
      const countriesData = Array.isArray(response.data) ? response.data : [];
      setCountries(countriesData);

      console.log("📍 User's saved country:", formData.country);
      console.log("📍 User's saved division:", formData.division);

      // If user already has a country selected, find its code and trigger division fetch
      if (formData.country && countriesData.length > 0) {
        const existingCountry = countriesData.find(
          (c: any) => c.name === formData.country
        );
        console.log("📍 Found existing country:", existingCountry);
        if (existingCountry) {
          setSelectedCountryId(existingCountry.code);
          // This will trigger the useEffect to fetch divisions
        }
      }
    } catch (error) {
      console.error("Failed to fetch countries:", error);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchDivisions = async (countryCode: string) => {
    try {
      setLoadingDivisions(true);
      const response = await apiClient.get(
        `${API_CONFIG.BASE_URL}/api/public/divisions?country_code=${countryCode}`
      );

      console.log("📍 Divisions response:", response.data);
      console.log("📍 Looking for division:", formData.division);

      // API returns array directly, not wrapped in {status, data}
      const divisionsData = Array.isArray(response.data) ? response.data : [];
      setDivisions(divisionsData);

      // If user already has a division selected, find its ID and trigger cities fetch
      if (formData.division && divisionsData.length > 0) {
        const existingDivision = divisionsData.find(
          (d: any) => d.name === formData.division
        );
        console.log("📍 Found existing division:", existingDivision);
        if (existingDivision) {
          setSelectedDivisionId(existingDivision.id);
          console.log("📍 Set division ID:", existingDivision.id);
          // This will trigger the useEffect to fetch cities
        } else {
          console.warn("📍 Division not found in list:", formData.division);
          console.warn("📍 The saved value might be a city name instead of division. Attempting to find the correct division...");

          // The saved value might be a city name, not a division
          // Try to find which division this city belongs to by checking all divisions
          findDivisionForCity(formData.division, divisionsData);
        }
      }
    } catch (error) {
      console.error("Failed to fetch divisions:", error);
      // If division fetch fails, reset divisions dropdown
      setDivisions([]);
    } finally {
      setLoadingDivisions(false);
    }
  };

  const fetchCities = async (divisionId: string) => {
    try {
      setLoadingCities(true);
      const response = await apiClient.get(
        `${API_CONFIG.BASE_URL}/api/public/cities?divisionId=${divisionId}`
      );

      console.log("Cities response:", response.data);

      // API returns array directly, not wrapped in {status, data}
      const citiesData = Array.isArray(response.data) ? response.data : [];
      setCities(citiesData);

      // If user already has a city selected, verify it exists in the fetched cities
      if (formData.city && citiesData.length > 0) {
        const cityExists = citiesData.some((c: any) => c.name === formData.city);
        if (!cityExists) {
          // City doesn't exist in this division, clear it
          console.warn(`City "${formData.city}" not found in division cities, clearing selection`);
          setFormData((prev) => ({ ...prev, city: "" }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      // If cities fetch fails, reset cities dropdown
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Function to find which division a city belongs to
  // This handles the edge case where the database contains a city name instead of a division name
  const findDivisionForCity = async (cityName: string, divisionsData: any[]) => {
    console.log("🔍 Attempting to find division for city:", cityName);

    // Iterate through all divisions to find which one contains this city
    for (const division of divisionsData) {
      try {
        console.log(`🔍 Checking division: ${division.name}`);
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}/api/public/cities?divisionId=${division.id}`
        );

        const citiesData = Array.isArray(response.data) ? response.data : [];

        // Check if the city exists in this division
        const foundCity = citiesData.find((c: any) => c.name === cityName);

        if (foundCity) {
          console.log("✅ Found city in division:", division.name);
          console.log("✅ Updating formData to use correct division name");

          // Update formData with the correct division name
          setFormData((prev) => ({ ...prev, division: division.name, city: cityName }));

          // Set the division ID to trigger cities fetch
          setSelectedDivisionId(division.id);

          return; // Exit after finding the correct division
        }
      } catch (error) {
        console.error(`Error fetching cities for division ${division.name}:`, error);
      }
    }

    console.warn("❌ Could not find division for city:", cityName);
    console.warn("❌ The city may not exist in the database or there's a data inconsistency");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    // SECURITY: Sanitize all form inputs before submission
    const sanitizationResult = FormSanitizer.sanitizeFormData(
      {
        name: formData.name,
        mobile: formData.mobile,
        address: formData.address,
        city: formData.division,
        country: formData.country,
        postcode: formData.postcode,
      },
      {
        name: 'text',
        mobile: 'phone',
        address: 'address',
        city: 'city',
        country: 'text',
        postcode: 'postcode',
      }
    );

    if (!sanitizationResult.isValid) {
      setStatus("error");
      setErrorMessage(
        t("account.invalidInput") +
          Object.values(sanitizationResult.errors).join(", ")
      );
      return;
    }

    try {
      // Get access token from storage
      const accessToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      // Prepare data for backend - map 'division' to 'city' field (use sanitized data)
      const dataToSubmit = {
        name: sanitizationResult.sanitizedData.name || formData.name,
        mobile: sanitizationResult.sanitizedData.mobile || formData.mobile,
        address: sanitizationResult.sanitizedData.address || formData.address,
        city: sanitizationResult.sanitizedData.city || formData.division, // Backend stores division name in 'city' field
        country: sanitizationResult.sanitizedData.country || formData.country,
        postcode: sanitizationResult.sanitizedData.postcode || formData.postcode,
      };

      const response = await updateProfile(accessToken, dataToSubmit);

      if (response.status) {
        setStatus("success");
        // Update user data with new values
        const updatedUser = {
          ...userData,
          ...formData,
        };
        // Call onSuccess with updated user data
        setTimeout(() => {
          onSuccess(updatedUser);
        }, 1500);
      } else {
        setStatus("error");
        setErrorMessage(response.message || t("account.profileUpdateFailed"));
      }
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          t("auth.genericError")
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Handle country selection
    if (name === "country") {
      // Find the selected country and get its code
      const selectedCountry = countries.find((c) => c.name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.code); // Use code, not id
      } else {
        setSelectedCountryId(null);
      }

      // Reset dependent fields
      setFormData((prev) => ({ ...prev, division: "", city: "" }));
      setDivisions([]);
      setCities([]);
      setSelectedDivisionId(null);
    }

    // Handle division selection
    if (name === "division") {
      // Find the selected division and get its ID
      const selectedDivision = divisions.find((d) => d.name === value);
      if (selectedDivision) {
        setSelectedDivisionId(selectedDivision.id);
      } else {
        setSelectedDivisionId(null);
      }

      // Reset cities when division changes
      setFormData((prev) => ({ ...prev, city: "" }));
      setCities([]);
    }
  };

  return (
    <div className="edit-profile-form">
      <h2
        className="mb-6 text-2xl font-bold text-brand-fg"
        style={headingFontStyle}
      >
        {t("account.editProfile")}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div className="form-group">
          <label
            htmlFor="name"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("auth.nameLabel")}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={inputClass}
            placeholder={t("auth.namePlaceholder")}
            disabled={status === "loading"}
          />
        </div>

        {/* Mobile Field */}
        <div className="form-group">
          <label
            htmlFor="mobile"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("account.mobileNumber")}
          </label>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            dir="ltr"
            value={formData.mobile}
            onChange={handleChange}
            className={inputClass}
            placeholder={t("auth.phonePlaceholder")}
            disabled={status === "loading"}
          />
        </div>

        {/* Address Field */}
        <div className="form-group">
          <label
            htmlFor="address"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("checkout.fieldAddress")}
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className={inputClass}
            placeholder={t("checkout.addressLine1Placeholder")}
            disabled={status === "loading"}
          />
        </div>

        {/* Country, Division (City), and City */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label
              htmlFor="country"
              className="mb-2 block text-sm font-medium text-brand-fg"
            >
              {t("checkout.fieldCountry")}
            </label>
            {loadingCountries ? (
              <div className="w-full rounded-brand border border-brand-border px-4 py-3 text-brand-muted">
                {t("account.loadingCountries")}
              </div>
            ) : (
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={inputClass}
                disabled={status === "loading"}
              >
                <option value="">{t("checkout.selectCountry")}</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label
              htmlFor="division"
              className="mb-2 block text-sm font-medium text-brand-fg"
            >
              {t("checkout.divisionState")}
            </label>
            {loadingDivisions ? (
              <div className="w-full rounded-brand border border-brand-border px-4 py-3 text-brand-muted">
                {t("account.loadingDivisions")}
              </div>
            ) : (
              <select
                id="division"
                name="division"
                value={formData.division}
                onChange={handleChange}
                className={inputClass}
                disabled={status === "loading" || !formData.country || loadingDivisions}
              >
                <option value="">
                  {!formData.country
                    ? t("account.selectCountryFirst")
                    : divisions.length === 0
                    ? t("account.noDivisions")
                    : t("checkout.selectDivision")}
                </option>
                {divisions.map((division: any) => (
                  <option key={division.id} value={division.name}>
                    {division.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-brand-muted">
              {formData.division
                ? t("account.selectedValue", { name: formData.division })
                : t("account.selectDivisionHint")}
            </p>
          </div>
        </div>

        {/* City Dropdown - Shows after division selection */}
        {formData.division && (
          <div className="form-group">
            <label
              htmlFor="city"
              className="mb-2 block text-sm font-medium text-brand-fg"
            >
              {t("checkout.cityDistrict")}
            </label>
            {loadingCities ? (
              <div className="w-full rounded-brand border border-brand-border px-4 py-3 text-brand-muted">
                {t("account.loadingCities")}
              </div>
            ) : (
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={inputClass}
                disabled={status === "loading" || loadingCities}
              >
                <option value="">
                  {cities.length === 0
                    ? t("account.noCities")
                    : t("account.selectCityOptional")}
                </option>
                {cities.map((city: any) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-brand-muted">
              {t("account.cityOptionalHint")}
            </p>
          </div>
        )}

        {/* Postcode Field */}
        <div className="form-group">
          <label
            htmlFor="postcode"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("checkout.fieldPostcode")}
          </label>
          <input
            type="text"
            id="postcode"
            name="postcode"
            dir="ltr"
            value={formData.postcode}
            onChange={handleChange}
            className={inputClass}
            placeholder="SW1A 1AA"
            disabled={status === "loading"}
          />
        </div>

        {/* Error Message */}
        {status === "error" && errorMessage && (
          <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-4">
            <p className="text-sm text-brand-danger">{errorMessage}</p>
          </div>
        )}

        {/* Success Message */}
        {status === "success" && (
          <div className="rounded-brand border border-brand-success/30 bg-brand-success/10 p-4">
            <p className="text-sm text-brand-success">
              {t("account.profileUpdated")}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <button
            type="submit"
            disabled={status === "loading"}
            className={`flex-1 rounded-brand px-4 py-3 font-semibold text-brand-primary-foreground transition-all ${
              status === "loading"
                ? "cursor-not-allowed bg-gray-400"
                : "bg-brand-primary hover:bg-brand-primary-hover"
            }`}
          >
            {status === "loading" ? (
              <span className="flex items-center justify-center">
                <svg
                  className="me-3 -ms-1 h-5 w-5 animate-spin text-current"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t("account.updating")}
              </span>
            ) : (
              t("account.updateProfile")
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={status === "loading"}
            className="rounded-brand border border-brand-border px-6 py-3 font-semibold text-brand-fg transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
